import { Table } from 'components/core-ui/Table'
import s from './CollectorReports.modules.scss'
import { useEffect, useMemo, useState } from 'react'
import { makeCoreGetRequest } from 'utils/api'
import { toTableGETUrl } from 'components/core-ui/Table/serializers'
import { toTableRowItems } from './serializers'
import { formatErrorMessage } from 'utils/formatting'
import { useNotification, usePermissions } from 'hooks'
import { handleQueryError } from 'utils/helpers'
import { getColumns, getFilters } from './helpers'
import { collectorReportsRoles } from '../constants'
import {
  AddActionIcon,
  UploadActionIcon,
  EditActionIcon,
  PrintActionIcon,
  DownloadActionIcon,
  DeleteActionIcon
} from 'icons'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'
import { CollectorReportModal } from './CollectorReportModal'
import { getCollectingCompanyOptions, getWasteCategoryOptions, downloadDocument } from '../api'
import { UploadFilesModal } from 'components/core-ui/UploadFilesModal'
import { Typography } from '@mui/material'
import useUpload from '../common/hooks/useUpload'
import { PrintPreviewModal } from '../common/PrintPreviewModal'

const CollectorReports = () => {
  const [tableItems, setTableItems] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [totalCount, setTotalCount] = useState<number>(0)
  const [isNewModalOpen, setIsNewModalOpen] = useState<boolean>(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
  const [isUploadFilesModalOpen, setIsUploadFilesModalOpen] = useState<boolean>(false)
  const [isPrintPreviewModalOpen, setIsPrintPreviewModalOpen] = useState<boolean>(false)
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null)
  const [isPdfBlobLoading, setIsPdfBlobLoading] = useState<boolean>(false)
  const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
  const [shouldResetPage, setShouldResetPage] = useState<boolean>(false)
  const [selectedRow, setSelectedRow] = useState<any>()
  const [shouldRefetchItem, setShouldRefetchItem] = useState(false)
  const [rowDownloadId, setRowDownloadId] = useState<number>(0)

  const { showNotification } = useNotification()

  const { canCreate, canEdit, canDelete } = usePermissions(collectorReportsRoles)

  const openNewModal = (): void => setIsNewModalOpen(true)
  const closeNewModal = (): void => setIsNewModalOpen(false)

  const openEditModal = (): void => {
    setIsEditModalOpen(true)
    setShouldRefetchItem(true)
  }
  const closeEditModal = (): void => {
    setIsEditModalOpen(false)
    setShouldRefetchItem(false)
  }

  const openDeleteModal = (): void => setIsDeleteModalOpen(true)
  const closeDeleteModal = (): void => setIsDeleteModalOpen(false)

  const openUploadFilesModal = (): void => setIsUploadFilesModalOpen(true)
  const closeUploadFilesModal = (): void => setIsUploadFilesModalOpen(false)

  const openPrintPreviewModal = (): void => setIsPrintPreviewModalOpen(true)
  const closePrintPreviewModal = (): void => {
    setIsPrintPreviewModalOpen(false)
    setPdfBlob(null)
  }

  const toolbarActions = useMemo(() => {
    if (canCreate) {
      return [
        {
          label: 'Adauga',
          icon: <AddActionIcon />,
          onClick: () => openNewModal()
        }
      ]
    }

    return []
  }, [canCreate])

  const { data: wasteCategoryOptions } = getWasteCategoryOptions(showNotification)

  const { data: companyOptions } = getCollectingCompanyOptions(showNotification)

  const downloadQuery = downloadDocument(showNotification, 'WASTE', rowDownloadId, 'PV', () =>
    setRowDownloadId(0)
  )

  useEffect(() => {
    if (rowDownloadId) {
      downloadQuery.refetch()
    }
  }, [rowDownloadId])

  const columns = useMemo(() => getColumns(), [])

  const filters = useMemo(
    () => getFilters(companyOptions, wasteCategoryOptions),
    [companyOptions, wasteCategoryOptions]
  )

  const selectRow = (rowId: number, action: string) => {
    const item = tableItems.find(item => item.id === rowId)
    setSelectedRow(item)

    switch (action) {
      case 'edit':
        openEditModal()
        break
      case 'upload':
        openUploadFilesModal()
        break
      case 'delete':
        openDeleteModal()
        break
      default:
        break
    }
  }

  const fetchData = async ({ page, rowsPerPage, order, orderBy, tableFilters }) => {
    setIsLoading(true)
    try {
      const response = await makeCoreGetRequest(
        toTableGETUrl({
          url: '/waste-management-system/waste-report-collection-company',
          filters: [{ name: 'currentLocation', value: true }],
          tableFilters,
          page,
          rowsPerPage,
          order,
          orderBy
        })
      )

      setTableItems(toTableRowItems(response.list || []))
      setTotalCount(response.page.totalCount)
      setIsLoading(false)
    } catch (error) {
      showNotification({
        message: `A aparut o eroare la obtinerea datelor pentru rapoartele de predare. ${formatErrorMessage(
          error
        )}`,
        type: 'error'
      })
    }
  }

  const { saveDocuments } = useUpload({
    reportId: selectedRow?.id,
    reportType: 'WASTE',
    onSuccess: () => {
      setShouldRefreshData(true)
      closeUploadFilesModal()
      showNotification({
        message: `Fisierele au fost incarcate cu success.`,
        type: 'success'
      })
    },
    onError: error => {
      handleQueryError(error, `A aparut o eroare la incarcarea fisierelor.`, showNotification)
    }
  })

  const printPDF = async (rowId: number) => {
    try {
      setIsPdfBlobLoading(true)
      openPrintPreviewModal()

      const response = await makeCoreGetRequest(
        `/waste-management-system/waste-report-collection-company/pdf/${rowId}`,
        { responseType: 'blob' }
      )

      const blob = new Blob([response], { type: 'application/pdf' })
      setPdfBlob(blob)
    } catch (error) {
      closePrintPreviewModal()
      showNotification({
        message: `A aparut o eroare la generarea raportului PDF. ${formatErrorMessage(error)}`,
        type: 'error'
      })
    } finally {
      setIsPdfBlobLoading(false)
    }
  }

  return (
    <div className={s.mainContainer}>
      {canCreate && (
        <CollectorReportModal
          isModalOpen={isNewModalOpen}
          closeModal={closeNewModal}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      {canEdit && (
        <CollectorReportModal
          isModalOpen={isEditModalOpen}
          closeModal={closeEditModal}
          editItemId={selectedRow?.id}
          shouldRefetchItem={shouldRefetchItem}
          setShouldRefreshData={setShouldRefreshData}
          isEdit
        />
      )}
      {canEdit && (
        <UploadFilesModal
          isModalOpen={isUploadFilesModalOpen}
          closeModal={closeUploadFilesModal}
          saveDocuments={saveDocuments}
          documentType={{ value: 'PV', label: 'Proces Verbal' }}
          accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
        />
      )}
      {canDelete && (
        <DeleteConfirmationModal
          isModalOpen={isDeleteModalOpen}
          closeModal={closeDeleteModal}
          actionUrl="/waste-management-system/waste-report-collection-company/:id"
          itemId={`${selectedRow?.id}`}
          content={<Typography>Sunteti sigur ca vreti sa stergeti acest raport?</Typography>}
          setShouldRefreshData={setShouldRefreshData}
        />
      )}
      <PrintPreviewModal
        isModalOpen={isPrintPreviewModalOpen}
        closeModal={closePrintPreviewModal}
        pdfBlob={pdfBlob}
        isPdfBlobLoading={isPdfBlobLoading}
      />
      <Table
        hideCheckbox
        orderBy="date"
        order="desc"
        columns={columns}
        items={tableItems}
        fetchData={fetchData}
        isLoading={isLoading}
        totalCount={totalCount}
        shouldRefreshData={shouldRefreshData}
        setShouldRefreshData={setShouldRefreshData}
        shouldResetPage={shouldResetPage}
        setShouldResetPage={setShouldResetPage}
        rowActions={[
          {
            label: 'Print',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#4A148C',
            desktopIcon: <PrintActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => printPDF(rowId),
            showTooltip: true
          },
          canEdit && {
            label: 'Upload',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#FFB300',
            desktopIcon: <UploadActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => selectRow(rowId, 'upload'),
            showTooltip: true
          },
          {
            label: 'Download',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#33691E',
            desktopIcon: <DownloadActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => setRowDownloadId(rowId),
            showTooltip: true
          },
          canEdit && {
            label: 'Editeaza',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#0970E6',
            desktopIcon: <EditActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => selectRow(rowId, 'edit'),
            hideCondition: { field: 'isHandedOver', value: true },
            showTooltip: true
          },
          canDelete && {
            label: 'Sterge',
            labelColor: 'white',
            typographyVariant: 'medium',
            backgroundColor: '#d9534f',
            desktopIcon: <DeleteActionIcon />,
            onClick: (_e: MouseEvent, rowId: number) => selectRow(rowId, 'delete'),
            hideCondition: { field: 'isHandedOver', value: true },
            showTooltip: true
          }
        ]}
        rowActionsWidth="350px"
        toolbarActions={toolbarActions}
        filters={filters}
        filtersLabel="Filtreaza"
        filtersButtonLabel="Filtreaza"
        appliedFiltersLabel="Filtre"
        cancelFiltersButtonLabel="Anuleaza"
        applyFiltersButtonLabel="Aplica"
      />
    </div>
  )
}

export default CollectorReports
